import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Bell, CheckCircle, MessageSquare, 
  ThumbsUp, Check, Trash2, RefreshCw, 
  AlertCircle, Calendar, ChevronLeft,
  ChevronRight
} from 'lucide-react';
import toast from 'react-hot-toast';

const NotificationsPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    hasMore: false
  });
  const [filterUnread, setFilterUnread] = useState(false);
  const navigate = useNavigate();
  
  useEffect(() => {
    fetchNotifications(1, filterUnread);
  }, [filterUnread]);
  
  const fetchNotifications = async (page, unreadOnly = false) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/notifications?page=${page}&limit=20&unreadOnly=${unreadOnly}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch notifications');
      }
      
      const data = await response.json();
      
      if (page === 1) {
        setNotifications(data.notifications);
      } else {
        setNotifications(prev => [...prev, ...data.notifications]);
      }
      
      setPagination(data.pagination);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setError('Failed to load notifications');
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };
  
  const handleMarkAsRead = async (notificationId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/notifications/${notificationId}/read`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to mark notification as read');
      }
      
      // Update notifications list
      setNotifications(notifications.map(notification => 
        notification.id === notificationId 
          ? { ...notification, isRead: true } 
          : notification
      ));
    } catch (error) {
      console.error('Error marking notification as read:', error);
      toast.error('Failed to mark notification as read');
    }
  };
  
  const handleMarkAllAsRead = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/notifications/read-all`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to mark all notifications as read');
      }
      
      // Update notifications list
      setNotifications(notifications.map(notification => ({ ...notification, isRead: true })));
      
      toast.success('All notifications marked as read');
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      toast.error('Failed to mark all notifications as read');
    }
  };
  
  const handleDeleteNotification = async (notificationId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/notifications/${notificationId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete notification');
      }
      
      // Remove notification from list
      setNotifications(notifications.filter(notification => notification.id !== notificationId));
      
      toast.success('Notification deleted');
    } catch (error) {
      console.error('Error deleting notification:', error);
      toast.error('Failed to delete notification');
    }
  };
  
  const handleNotificationClick = (notification) => {
    // Mark as read if not already
    if (!notification.isRead) {
      handleMarkAsRead(notification.id);
    }

    // Parse metadata if available
    let metadata = {};
    try {
      if (notification.metadata) {
        metadata = JSON.parse(notification.metadata);
      }
    } catch (error) {
      console.error('Error parsing notification metadata:', error);
    }
    
    // Navigate to the appropriate page based on notification type
    const { type, referenceId, referenceType } = notification;
    
    // Find course ID if applicable
    let courseId = null;
    let questionId = null;
    
    if (metadata.courseId) {
      if (referenceType === 'question' || type.includes('question_vote')) {
        // Direct link to the question - use referenceId which is the question ID
        navigate(`/courses/${metadata.courseId}/questions/${referenceId}`);
      } else if (referenceType === 'answer' || type.includes('answer')) {
        // For answers, we need to navigate to the question that contains the answer
        // Important: Use metadata.questionId, NOT the referenceId (which is the answer ID)
        if (metadata.questionId) {
          navigate(`/courses/${metadata.courseId}/questions/${metadata.questionId}`);
        } else {
          // Fallback if we don't have the question ID
          console.warn('Missing questionId in notification metadata for answer reference:', notification);
          toast.warning('Could not navigate to the specific content');
        }
      } else {
        // Default to notifications page
        navigate('/notifications');
      }
    } else {
      // Log warning if no metadata
      console.warn('Missing courseId in notification metadata:', notification);
      toast.warning('Could not navigate to the specific content');
    }
  };
  
  // Render notification icon based on type
  const renderNotificationIcon = (notification) => {
    const { type } = notification;
    
    if (type === 'answer_accepted') {
      return <CheckCircle size={20} className="text-green-500 dark:text-green-400" />;
    } else if (type.includes('answer')) {
      return <MessageSquare size={20} className="text-blue-500 dark:text-blue-400" />;
    } else if (type.includes('vote')) {
      return <ThumbsUp size={20} className="text-red-500 dark:text-red-400" />;
    } else {
      return <Bell size={20} className="text-gray-500 dark:text-gray-400" />;
    }
  };
  
  // Format date
  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString(undefined, { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Helper function to check if notification has rejection notes
  const hasRejectionNotes = (notification) => {
    if (!notification.type.includes('rejected')) return false;
    
    try {
      const metadata = JSON.parse(notification.metadata || '{}');
      return metadata.rejectionNotes && metadata.rejectionNotes.trim().length > 0;
    } catch (error) {
      return false;
    }
  };

  // Helper function to get rejection notes
  const getRejectionNotes = (notification) => {
    try {
      const metadata = JSON.parse(notification.metadata || '{}');
      return metadata.rejectionNotes;
    } catch (error) {
      return null;
    }
  };
  
  const handlePageChange = (newPage) => {
    fetchNotifications(newPage, filterUnread);
  };
  
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Notifications</h1>
          
          <div className="flex items-center space-x-2">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="filter-unread"
                checked={filterUnread}
                onChange={(e) => setFilterUnread(e.target.checked)}
                className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
              />
              <label htmlFor="filter-unread" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Unread only
              </label>
            </div>
            
            <button
              onClick={() => fetchNotifications(1, filterUnread)}
              className={`p-2 text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 rounded-full ${
                loading ? 'animate-spin' : ''
              }`}
              title="Refresh"
              disabled={loading}
            >
              <RefreshCw size={18} />
            </button>
            
            <button
              onClick={handleMarkAllAsRead}
              className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md text-sm flex items-center"
              disabled={notifications.every(n => n.isRead)}
            >
              <Check size={16} className="mr-1" />
              Mark All Read
            </button>
          </div>
        </div>
        
        {/* Notification List */}
        {loading && notifications.length === 0 ? (
          <div className="flex justify-center items-center py-12">
            <div className="w-6 h-6 border-t-2 border-b-2 border-red-500 rounded-full animate-spin mr-3"></div>
            <span className="text-gray-600 dark:text-gray-400">Loading notifications...</span>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-12">
            <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
            <h2 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
              Error Loading Notifications
            </h2>
            <p className="text-gray-600 dark:text-gray-400 text-center max-w-md mb-4">
              {error}
            </p>
            <button
              onClick={() => fetchNotifications(1, filterUnread)}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
            >
              Try Again
            </button>
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Bell className="h-12 w-12 text-gray-400 dark:text-gray-600 mb-4" />
            <h2 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
              No Notifications
            </h2>
            <p className="text-gray-600 dark:text-gray-400 text-center max-w-md">
              {filterUnread 
                ? 'You have no unread notifications. Try viewing all notifications.'
                : 'You have no notifications yet. They will appear here when you receive them.'
              }
            </p>
            {filterUnread && (
              <button
                onClick={() => setFilterUnread(false)}
                className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                View All Notifications
              </button>
            )}
          </div>
        ) : (
          <ul className="divide-y divide-gray-200 dark:divide-gray-700">
            {notifications.map(notification => (
              <li 
                key={notification.id} 
                className={`relative ${
                  notification.isRead 
                    ? 'bg-white dark:bg-gray-800' 
                    : 'bg-blue-50 dark:bg-blue-900/20'
                } hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors`}
              >
                <div className="px-6 py-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 pt-1">
                      {renderNotificationIcon(notification)}
                    </div>
                    <div 
                      className="ml-4 flex-1 cursor-pointer"
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <p className="text-sm text-gray-800 dark:text-gray-200">
                        {notification.message}
                      </p>
                      
                      {/* Rejection Notes */}
                      {hasRejectionNotes(notification) && (
                        <div className="mt-3 pl-4 border-l-2 border-gray-300 dark:border-gray-600">
                          <p className="text-sm text-gray-600 dark:text-gray-400 italic">
                            Reason: {getRejectionNotes(notification)}
                          </p>
                        </div>
                      )}
                      
                      <div className="mt-1 flex items-center text-xs text-gray-500 dark:text-gray-400">
                        <Calendar size={14} className="mr-1" />
                        <span>{formatDate(notification.createdAt)}</span>
                      </div>
                    </div>
                    <div className="flex-shrink-0 flex space-x-1">
                      {!notification.isRead && (
                        <button
                          onClick={() => handleMarkAsRead(notification.id)}
                          className="p-1 text-gray-500 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400 rounded-full"
                          title="Mark as read"
                        >
                          <Check size={18} />
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteNotification(notification.id)}
                        className="p-1 text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 rounded-full"
                        title="Delete notification"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
        
        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
              className={`flex items-center px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm ${
                pagination.page === 1
                  ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <ChevronLeft size={16} className="mr-1" />
              Previous
            </button>
            
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            
            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={!pagination.hasMore}
              className={`flex items-center px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm ${
                !pagination.hasMore
                  ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              Next
              <ChevronRight size={16} className="ml-1" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;